import AppIcons from "../../../icons";
import { ScanPackagesView } from "../scan_packages";
import { ReceiveItemView } from "./partials/receive_item";
import { Template } from "../../../../core/template";
import { pad } from "../../../../core/helpers/string";
import { precision } from "../../../../core/helpers/math";
import { FortumApp } from "../../../fortumApp";
import { emptyElement } from "../../../../core/helpers/dom";
import { ClientAction } from "../../../synchronizer";
import ReceiveConfirmModal from "../modals/receive_confirm";
import { ModalResultType } from "../../../../core/modal";


export class ScanReceivePackagesView extends ScanPackagesView {

    app: FortumApp;
    template = Template.lazy('/app/views/package_tracking/receive/index.html');

    // Item view
    itemViewClass = ReceiveItemView;

    // Next view's hash
    nextViewHash = undefined;

    prefixInput: HTMLInputElement;
    codeInput: HTMLInputElement;
    postfixInput: HTMLInputElement;

    receivableWrapper: HTMLDivElement;
    receivableList: HTMLUListElement;
    receivedWrapper: HTMLDivElement;
    receivedList: HTMLUListElement;

    public afterRender(): PromiseLike<any>
    {
        let year = new Date().getFullYear().toString();
        this.postfixInput.value = "LO" + year.substring(2);
        this.codeInput.addEventListener('input', () => {
            if (this.codeInput.value.length >= 4) {
                console.log('Auto submit');
                this.codeInput.blur();
                this.onCodeScanned(this.codeInput.value);
            }
        });

        return super.afterRender();
    }

    public beforeRender(): PromiseLike<any>
    {
        super.beforeRender();
        this.app.header.text = this.pgettext('Title', 'Receive packages');
        this.app.header.iconClass = AppIcons.PackageTracking_Receive;
        return;
    }

    protected clearResults() {
        this.resultRows.length = 0;
        emptyElement(this.receivableList);
        emptyElement(this.receivedList);
    }

    /**
     * Called when a new item needs to be rendered
     * @param scannedItem Item to render
     */
    protected renderScanned(scannedItem: ReceiveItemView)
    {
        return this.app.renderPartialView(scannedItem).then(() => {
            this.resultRows.push(scannedItem);
            if (scannedItem.item.received_quantity == 0 || scannedItem.item.received_quantity == null) {
                this.receivableList.insertAdjacentElement('afterbegin', scannedItem.element);
            } else {
                this.receivedList.insertAdjacentElement('afterbegin', scannedItem.element);
            }
        });
    }

    /**
     * Called when code is scanned 
     * @param code 
     */
    protected onCodeScanned(code: string)
    {   
        // Clear current list
        this.clearResults();
        
        // Combine code parts
        code = this.prefixInput.value + pad(code, 4) + this.postfixInput.value;
        
        // Query delivery note items (locally) and render
        this.app.db.DeliveryNoteItem.where('delivery_note_id').equals(code).toArray().then(items => {
            const promises = [];
            if (items.length > 0) {
                let temp_items = []
                items.forEach(item => {
                    if (item.received_quantity == 0 || item.received_quantity == null || item.received_quantity == item.quantity) {
                        temp_items.push(item);
                    } else {
                        let received = JSON.parse(JSON.stringify(item));
                        let stillLeft = JSON.parse(JSON.stringify(item));
                        received.oldReceived = received.received_quantity;
                        temp_items.push(received);
                        const numberOfDecimals = precision(stillLeft.quantity);
                        stillLeft.quantity = (stillLeft.quantity - stillLeft.received_quantity).toFixed(numberOfDecimals);
                        stillLeft.oldReceived = stillLeft.received_quantity;
                        stillLeft.received_quantity = 0;
                        temp_items.push(stillLeft);
                    }
                });

                temp_items.forEach(item => {
                    let scannedItem = new this.itemViewClass(item) as ReceiveItemView;
                    promises.push(this.renderScanned(scannedItem));
                });
            } else {
                this.app.toasts.show(this.pgettext('Message', 'Delivery note not found!'), 3000);
                this.codeInput.value = '';
                this.codeInput.focus();
            }
            return Promise.all(promises);
        }).then(() => {
            // Hide unncessary list(s)
            if (this.receivableList.querySelectorAll('li').length > 0) {
                this.receivableWrapper.classList.remove('hidden');
                this.nextButton.disabled = false;
            } else {
                this.receivableWrapper.classList.add('hidden');
                this.nextButton.disabled = true;
            }
    
            if (this.receivedList.querySelectorAll('li').length > 0) {
                this.receivedWrapper.classList.remove('hidden');
            } else {
                this.receivedWrapper.classList.add('hidden');
            }
        });
    }

    protected onContinue(data: any[]) {

        // Filter out rows that are not checked or have been fully received
        data = data.filter((element) => {
            let alreadyReceived = element.received_quantity != null && element.received_quantity > 0;
            return element.isSelected == true && alreadyReceived == false;
        });
        if (data.length == 0) {
            this.app.toasts.show(this.pgettext('Message', 'At least one row must be selected before receive is possible'));
            return;
        }

        new ReceiveConfirmModal(data).showIn(this.app).then(res => {

            if (res && res.type === ModalResultType.CANCEL) {
                return;
            }
        
            const promises = [];
            this.nextButton.disabled = true;
            const messages = [];
            data.forEach(item => {
                // skip already fully received rows
                if (item.received_quantity == item.quantity) {
                    this.app.toasts.show(this.pgettext('Message', `Package ${item.item_description} was already fully received`));
                    return;
                }

                let receiveMessage = {
                    id: parseInt(item.id),
                    quantity: parseFloat(item.quantity),
                    shipment_number: item.shipment_number,
                    waybill: item.waybill,
                    weight: parseFloat(item.weight),
                    weight_unit: item.weight_unit,
                    work_order: item.work_order
                };

                messages.push(JSON.stringify(receiveMessage));

                // Update local state
                let rcvQty = 0;
                if (item.oldReceived != null) {
                    rcvQty = item.oldReceived;
                }
                promises.push(this.app.db.DeliveryNoteItem.update(receiveMessage.id, {
                    received_quantity: rcvQty + receiveMessage.quantity
                }));
            });

            // Create sync message
            promises.push(this.app.synchronizer.push({
                action: ClientAction.PACKAGE_TRACKING.RECEIVE_DELIVERY_NOTE_ITEM,
                data: messages
            }, false));

            Promise.all(promises).then(() => {
                if (promises.length > 0) {
                    this.app.toasts.show(this.pgettext('Message', 'Packages received successfully'));
                    history.go(-1);
                }
            }).then(() => this.nextButton.disabled = false, () => this.nextButton.disabled = false);
        });
    }

}